/****************************************************************************
 * COPYRIGHT (c) 2024 Harman Connected Services
 *
 * All rights reserved
 *
 * This software embodies materials and concepts which are
 * confidential to Ignite Product and is made available solely
 * pursuant to the terms of a written license agreement with
 * Ignite Product.
 *
 * Designed and Developed by Harman International Industries, Inc.
 ***************************************************************************/

import Button from "@mui/material/Button";
import { styled } from "@mui/material/styles";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import {
  Checkbox,
  FormControlLabel,
  FormGroup,
  TextField,
} from "@mui/material";
import { useMemo, useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
}));

const CPUTypeModal = ({
  openCPUTypeModal,
  handleCloseCPUTypeModal,
  disabledVal,
  onSave,
  existingSelections = [],
}) => {
  const { architechures: cpuData } = useSelector((state) => state.cpuDetails);

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCpuTypes, setSelectedCpuTypes] = useState(existingSelections);

  // Memoize cpuDataWithoutAll to prevent unnecessary re-renders
  const cpuDataWithoutAll = useMemo(() => {
    return cpuData.filter((item) => item.toUpperCase() !== "ALL");
  }, [cpuData]);

  // Sync the selections only when modal opens
  useEffect(() => {
    if (!openCPUTypeModal) return;
    
    const hasAllFromBackend = existingSelections.some(
      (item) => item.toUpperCase() === "ALL"
    );

    if (hasAllFromBackend) {
      setSelectedCpuTypes([...cpuDataWithoutAll]);
    } else if (
      existingSelections.length === cpuDataWithoutAll.length &&
      cpuDataWithoutAll.every((cpu) => existingSelections.includes(cpu))
    ) {
      setSelectedCpuTypes([...cpuDataWithoutAll]); // All selected
    } else {
      // Filter out "ALL" from existing selections if it exists (shouldn't happen now)
      const filteredSelections = existingSelections.filter(
        (item) => item.toUpperCase() !== "ALL"
      );
      setSelectedCpuTypes(filteredSelections);
    }
  }, [openCPUTypeModal]);

  // Handle checkbox change for CPU types
  const handleCpuTypeChange = (cpuType, isChecked) => {
    if (disabledVal) return; // Prevent changes in read-only mode

    if (isChecked) {
      setSelectedCpuTypes((prev) => {
        const newSelection = [...prev, cpuType];
        return newSelection;
      });
    } else {
      setSelectedCpuTypes((prev) => {
        const newSelection = prev.filter((type) => type !== cpuType);
        return newSelection;
      });
    }
  };

  // Handle ALL checkbox change
  const handleAllChange = (isChecked) => {
    if (disabledVal) return; // Prevent changes in read-only mode

    if (isChecked) {
      // Select all CPU types (excluding "ALL" as it's not a real CPU type)
      setSelectedCpuTypes([...cpuDataWithoutAll]);
    } else {
      // Deselect all CPU types
      setSelectedCpuTypes([]);
    }
  };

  // Check if all CPU types are selected (represents "ALL")
  const isAllSelected =
    selectedCpuTypes.length === cpuDataWithoutAll.length &&
    cpuDataWithoutAll.every((cpu) => selectedCpuTypes.includes(cpu));

  // Handle save functionality
  const handleSave = () => {
    if (onSave) {
      let finalSelection;

      if (isAllSelected) {
        finalSelection = ["ALL"];
      } else {
        finalSelection = selectedCpuTypes;
      }

      onSave("architectureTypesInclusions", finalSelection);
    }
    handleCloseCPUTypeModal();
  };

  const filteredData = useMemo(() => {
    return searchTerm
      ? cpuDataWithoutAll.filter((item) =>
          item.toLowerCase().includes(searchTerm.toLowerCase())
        )
      : cpuDataWithoutAll;
  }, [searchTerm, cpuDataWithoutAll]);

  return (
      <BootstrapDialog
        slotProps={{
          paper: {
            sx: {
              minWidth: "35vw",
              maxHeight: "80vh",
              maxWidth: "none",
            },
          },
        }}
        onClose={handleCloseCPUTypeModal}
        aria-labelledby="customized-dialog-title"
        open={openCPUTypeModal}
      >
        <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title">
          Targeting - Cpu Types
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={handleCloseCPUTypeModal}
          sx={(theme) => ({
            position: "absolute",
            right: 8,
            top: 8,
            color: theme.palette.grey[500],
          })}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent dividers>
          <div>
            <TextField
              onChange={($event) => setSearchTerm($event.target.value)}
              id="outlined-textarea"
              label="Filter"
              placeholder="Search"
              sx={{
                height: "40px",
                padding: "10px 0px",
              }}
            />
            <FormGroup>
              {/* ALL checkbox */}
              <FormControlLabel
                control={
                  <Checkbox
                    disabled={disabledVal}
                    checked={isAllSelected}
                    onChange={(e) =>
                      !disabledVal && handleAllChange(e.target.checked)
                    }
                  />
                }
                label="ALL"
              />
              {filteredData?.map((cpu) => (
                <FormControlLabel
                  key={cpu}
                  control={
                    <Checkbox
                      disabled={disabledVal}
                      checked={selectedCpuTypes.includes(cpu)}
                      onChange={(e) =>
                        !disabledVal &&
                        handleCpuTypeChange(cpu, e.target.checked)
                      }
                    />
                  }
                  label={cpu}
                />
              ))}
            </FormGroup>
          </div>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            autoFocus
            onClick={handleCloseCPUTypeModal}
          >
            Close
          </Button>
          {!disabledVal && (
            <Button 
              variant="outlined" 
              autoFocus 
              onClick={handleSave}
              disabled={selectedCpuTypes.length === 0}
            >
              Save
            </Button>
          )}
        </DialogActions>
      </BootstrapDialog>
  );
};

CPUTypeModal.propTypes = {
  openCPUTypeModal: PropTypes.bool.isRequired,
  handleCloseCPUTypeModal: PropTypes.func.isRequired,
  disabledVal: PropTypes.bool,
  onSave: PropTypes.func,
  existingSelections: PropTypes.arrayOf(PropTypes.string),
};

export default CPUTypeModal;
